import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CricketHeader } from '#/components/layout/CricketHeader'
import { BottomNav } from '#/components/layout/BottomNav'
import { useCricketStore } from '#/store/cricketStore'
import { formatOvers } from '#/lib/utils'
import { Trash2, Download } from 'lucide-react'
import { useState } from 'react'
import { ConfirmDialog } from '#/components/ui/dialog'

export const Route = createFileRoute('/history')({
  validateSearch: (search: Record<string, unknown>): { mode?: 'edit' | 'view'; token?: string } => {
    return {
      mode: (search.mode as 'edit' | 'view') || 'edit',
      token: (search.token as string) || '',
    }
  },
  component: HistoryScreen,
})

export function getTeamInitials(name: string): string {
  return (
    name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || name.slice(0, 2).toUpperCase()
  )
}

function HistoryScreen() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const isViewOnly = search.mode === 'view'
  const savedMatches = useCricketStore((state) => state.savedMatches)
  const createdMatchIds = useCricketStore((state) => state.createdMatchIds || [])
  const syncMatchState = useCricketStore((state) => state.syncMatchState)
  const deleteMatch = useCricketStore((state) => state.deleteMatch)

  const [matchToDelete, setMatchToDelete] = useState<string | null>(null)

  return (
    <div className="flex-1 flex flex-col min-h-screen pb-20 bg-[#f5f6f8]">
      <CricketHeader title="Cricket scorer" />

      <div className="p-4 space-y-3 flex-1">
        {savedMatches.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-28 text-center text-gray-500">
            <p className="text-base font-medium">No matches scored yet.</p>
            <p className="text-xs text-gray-400 mt-1">Start a new match to see it here.</p>
          </div>
        ) : (
          savedMatches.map((m) => {
            const isCompleted = m.status === 'completed'
            const isOwner = createdMatchIds.includes(m.id)
            const canResume = !isCompleted && !isViewOnly && isOwner
            const tossText = `${m.tossWinner === 'host' ? m.hostTeam : m.visitorTeam} won the toss and opted to ${
              m.tossDecision === 'bat' ? 'Bat' : 'Bowl'
            } first.`

            const inn1Runs = m.innings1 ? m.innings1.totalRuns : m.totalRuns
            const inn1Wkts = m.innings1 ? m.innings1.totalWickets : m.totalWickets
            const inn1Balls = m.innings1 ? m.innings1.legalBallsDelivered : m.legalBallsDelivered

            const inn2Runs = m.innings2 ? m.innings2.totalRuns : 0
            const inn2Wkts = m.innings2 ? m.innings2.totalWickets : 0
            const inn2Balls = m.innings2 ? m.innings2.legalBallsDelivered : 0

            const hostInitials = getTeamInitials(m.hostTeam)
            const visitorInitials = getTeamInitials(m.visitorTeam)

            return (
              <div
                key={m.id}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-2.5"
              >
                {/* Date & Time Header */}
                <div className="text-xs text-gray-500 font-medium">
                  {m.createdAt || '27/10/2026 - 08:37 PM'}
                </div>

                {/* Team 1 Score Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="size-8 rounded-full bg-[#C0CA33] text-white flex items-center justify-center font-bold text-xs shadow-xs">
                      {hostInitials}
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">
                      {m.hostTeam}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {inn1Runs}/{inn1Wkts}{' '}
                    <span className="text-xs font-normal text-gray-500">
                      ({formatOvers(inn1Balls)})
                    </span>
                  </div>
                </div>

                {/* Team 2 Score Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="size-8 rounded-full bg-[#7CB342] text-white flex items-center justify-center font-bold text-xs shadow-xs">
                      {visitorInitials}
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">
                      {m.visitorTeam}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {inn2Runs}/{inn2Wkts}{' '}
                    <span className="text-xs font-normal text-gray-500">
                      ({formatOvers(inn2Balls)})
                    </span>
                  </div>
                </div>

                {/* Winner Text (Screen 21) or Toss Note */}
                {m.winnerText ? (
                  <div className="text-xs font-medium text-gray-800 pt-1">
                    {m.winnerText}
                  </div>
                ) : (
                  <div className="text-xs text-gray-600 font-medium pt-1">
                    {tossText}
                  </div>
                )}

                {/* Bottom Actions Row: If completed, exactly like Screen 21 (Scoreboard, Download, Trash) */}
                <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                  {canResume ? (
                    <button
                      onClick={() => {
                        syncMatchState(m)
                        navigate({ to: '/match/$matchId', params: { matchId: m.id } })
                      }}
                      className="text-sm font-semibold text-gray-800 hover:text-[#2E7D32] transition-colors"
                    >
                      Resume
                    </button>
                  ) : (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500">
                      {isCompleted ? 'Finished' : 'View Only'}
                    </span>
                  )}

                  <button
                    onClick={() => {
                      syncMatchState(m)
                      navigate({
                        to: '/match/$matchId/scorecard',
                        params: { matchId: m.id },
                        search: search.mode ? { mode: search.mode, token: search.token } : undefined,
                      })
                    }}
                    className="text-sm font-semibold text-gray-800 hover:text-[#2E7D32] transition-colors"
                  >
                    Scoreboard
                  </button>

                  <div className="flex items-center gap-3 text-gray-500">
                    <button
                      onClick={() => {
                        syncMatchState(m)
                        navigate({
                          to: '/match/$matchId/scorecard',
                          params: { matchId: m.id },
                          search: search.mode ? { mode: search.mode, token: search.token } : undefined,
                        })
                      }}
                      className="p-1 hover:text-gray-800 transition-colors"
                      title="Export / Download"
                    >
                      <Download className="size-4.5 text-gray-600" />
                    </button>
                    {!isViewOnly && (
                      <button
                        onClick={() => setMatchToDelete(m.id)}
                        className="p-1 hover:text-red-600 transition-colors"
                        title="Delete match"
                      >
                        <Trash2 className="size-4.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <ConfirmDialog
        open={Boolean(matchToDelete)}
        onOpenChange={(open) => !open && setMatchToDelete(null)}
        title="Delete Match"
        description="Are you sure you want to delete this match from your history? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          if (matchToDelete) {
            deleteMatch(matchToDelete)
            setMatchToDelete(null)
          }
        }}
      />

      <BottomNav />
    </div>
  )
}
